<script setup lang="ts">
import { ref, defineAsyncComponent } from 'vue';

const props = defineProps<{
    // Delete Modal
    showDeleteModal: boolean;
    movementToDelete: any;
    // Category Modal
    showCategoryModal: boolean;
    movementCategories: any[];
    creatingCat: boolean;
    // Clear History Modal
    showClearHistoryModal: boolean;
    clearingHistory: boolean;
    // Delete Import Modal
    showDeleteImportModal: boolean;
    // Common
    investmentName: string;
}>();

const emit = defineEmits([
    'update:showDeleteModal', 'perform-delete',
    'update:showCategoryModal', 'create-category',
    'update:showClearHistoryModal', 'clear-history',
    'update:showDeleteImportModal', 'delete-import'
]);

const newCatName = ref('');
const newCatParentId = ref<number | null>(null);
const clearHistoryConfirmation = ref('');

const handleCreateCategory = () => {
    emit('create-category', { name: newCatName.value, parentId: newCatParentId.value });
    newCatName.value = '';
    newCatParentId.value = null;
};

const handleClearHistory = () => {
    emit('clear-history');
    clearHistoryConfirmation.value = '';
};
</script>

<template>
    <div>
        <!-- Delete movement modal -->
        <Teleport to="body">
            <div v-if="showDeleteModal" class="fixed inset-0 z-[1050] flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm animate-in fade-in duration-300 overflow-y-auto" @mousedown.self="$emit('update:showDeleteModal', false)"
                 role="dialog" aria-modal="true" aria-labelledby="delete-modal-title">
                <div class="w-full shadow-2xl border border-border animate-in zoom-in-95 self-center bg-card rounded-2xl p-6 overflow-hidden my-8" style="max-width: 450px;">
                <h3 id="delete-modal-title" class="text-danger font-bold text-xl mb-2">Supprimer le Mouvement</h3>
                <p class="mb-4 text-muted">Vous êtes sur le point de supprimer un mouvement <span v-if="movementToDelete?.recurrenceGroupId" class="font-bold underline">récurrent</span>.</p>
                <div class="modal-actions-col space-y-3">
                    <button v-if="movementToDelete?.recurrenceGroupId" class="btn-delete-option danger-heavy w-full text-left p-4 rounded-xl border border-red-200 bg-red-50 hover:bg-red-100 transition-colors" @click="$emit('perform-delete', true)" aria-label="Supprimer toute la série récurrente">
                        <strong class="block text-red-700">Supprimer TOUTE LA SÉRIE</strong>
                        <span class="block text-xs font-normal text-red-600">Supprime ce mouvement et toutes ses futures occurrences.</span>
                    </button>
                    <button class="btn-delete-option danger-light w-full text-left p-4 rounded-xl border border-zinc-200 bg-zinc-50 hover:bg-zinc-100 transition-colors" @click="$emit('perform-delete', false)" aria-label="Supprimer uniquement ce mouvement">
                        <strong class="block text-zinc-700">Supprimer UNIQUEMENT CE MOUVEMENT</strong>
                        <span class="block text-xs font-normal text-zinc-600">Les autres occurrences resteront intactes.</span>
                    </button>
                    <button class="btn-secondary w-full py-3 rounded-xl border border-zinc-200 hover:bg-zinc-50 transition-colors font-bold" @click="$emit('update:showDeleteModal', false)">Annuler</button>
                </div>
                </div>
            </div>
        </Teleport>

        <!-- New category modal -->
        <Teleport to="body">
            <div v-if="showCategoryModal" class="fixed inset-0 z-[1060] flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm animate-in fade-in duration-300 overflow-y-auto" @mousedown.self="$emit('update:showCategoryModal', false)"
                 role="dialog" aria-modal="true" aria-labelledby="cat-modal-title">
                <div class="w-full animate-in zoom-in-95 self-center bg-card p-8 rounded-2xl shadow-2xl border border-border my-8" style="max-width: 400px;">
                <h3 id="cat-modal-title" class="text-xl font-bold mb-6 uppercase tracking-tight">Créer une catégorie</h3>
                <div class="space-y-4">
                    <div class="form-group space-y-2">
                        <label class="text-[10px] font-black uppercase tracking-widest text-muted-foreground ml-1">Nom</label>
                        <input type="text" v-model="newCatName" placeholder="Ex: Assurance" class="w-full bg-muted border border-border rounded-xl p-4 font-bold focus:ring-4 focus:ring-primary/10 outline-none transition-all" />
                    </div>
                    <div class="form-group space-y-2">
                        <label class="text-[10px] font-black uppercase tracking-widest text-muted-foreground ml-1">Parent (Optionnel)</label>
                        <select v-model="newCatParentId" class="w-full bg-muted border border-border rounded-xl p-4 font-bold focus:ring-4 focus:ring-primary/10 outline-none transition-all appearance-none">
                            <option :value="null">-- Aucun --</option>
                            <option v-for="cat in movementCategories" :key="cat.id" :value="cat.id">{{ cat.displayName }}</option>
                        </select>
                    </div>
                </div>
                <div class="modal-actions mt-8 flex gap-3">
                    <button class="btn-secondary flex-1 py-3 rounded-xl border border-border font-bold hover:bg-muted" type="button" @click="$emit('update:showCategoryModal', false)">Annuler</button>
                    <button class="btn-primary flex-1 py-3 rounded-xl bg-primary text-primary-foreground font-black uppercase tracking-widest text-xs shadow-lg disabled:opacity-50" type="button" @click="handleCreateCategory" :disabled="!newCatName || creatingCat">Créer</button>
                </div>
                </div>
            </div>
        </Teleport>

        <!-- Clear history modal -->
        <Teleport to="body">
            <div v-if="showClearHistoryModal" class="fixed inset-0 z-[1050] flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm animate-in fade-in duration-300 overflow-y-auto" @mousedown.self="$emit('update:showClearHistoryModal', false)"
                 role="dialog" aria-modal="true" aria-labelledby="clear-history-title">
                <div class="w-full shadow-2xl border border-border animate-in zoom-in-95 self-center bg-card rounded-2xl p-6 overflow-hidden my-8" style="max-width: 450px;">
                <h3 id="clear-history-title" class="text-danger">Vider l'historique</h3>
                <p class="mb-4 text-muted">Vous êtes sur le point de <strong class="text-danger">supprimer définitivement TOUS les mouvements</strong> liés à ce placement. Cette action est irréversible.</p>
                <div class="form-group mb-4">
                    <label>Écrivez "je confirme" pour valider :</label>
                    <input type="text" v-model="clearHistoryConfirmation" class="w-full p-2 border rounded" placeholder="je confirme">
                </div>
                <div class="modal-actions mt-4">
                    <button class="btn-secondary" @click="$emit('update:showClearHistoryModal', false); clearHistoryConfirmation = ''">Annuler</button>
                    <button class="btn-primary" style="background:var(--fin-danger);" :disabled="clearHistoryConfirmation !== 'je confirme' || clearingHistory" @click="handleClearHistory">
                        <span v-if="clearingHistory"><i class="fa-solid fa-spinner fa-spin"></i> Traitement...</span>
                        <span v-else>Tout Supprimer</span>
                    </button>
                </div>
                </div>
            </div>
        </Teleport>

        <!-- Delete import modal -->
        <Teleport to="body">
            <div v-if="showDeleteImportModal" class="fixed inset-0 z-[1050] flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm animate-in fade-in duration-300 overflow-y-auto" @mousedown.self="$emit('update:showDeleteImportModal', false)"
                 role="dialog" aria-modal="true" aria-labelledby="delete-import-title">
                <div class="w-full shadow-2xl border border-border animate-in zoom-in-95 self-center bg-card rounded-2xl p-6 overflow-hidden my-8" style="max-width: 350px;">
                <h3 id="delete-import-title" class="text-danger">Annuler l'Import</h3>
                <p class="mb-4">Vos mouvements ajoutés via ce fichier CSV vont être tous supprimés. Confirmer ?</p>
                <div class="modal-actions">
                    <button class="btn-secondary" @click="$emit('update:showDeleteImportModal', false)">Annuler</button>
                    <button class="btn-primary" style="background:var(--fin-danger);" @click="$emit('delete-import')">Confirmer</button>
                </div>
                </div>
            </div>
        </Teleport>
    </div>
</template>
